import { useState } from 'react';
import { Plus, Send } from 'lucide-react';
import {
  IconFileText,
  IconUpload,
  IconBrandYoutube,
  IconPresentation,
  IconQuestionMark,
  IconSparkles
} from '@tabler/icons-react';

export interface ActionCard {
  id: string;
  label: string;
  icon: React.ElementType;
  bgColor: string;
  iconColor: string;
  beta?: boolean;
}

// Shared action cards definition - single source of truth
export const actionCards: ActionCard[] = [
  {
    id: 'slide-deck',
    label: 'Explainer',
    icon: IconPresentation,
    bgColor: 'bg-[#fffbeb]',
    iconColor: 'text-amber-500',
  },
  {
    id: 'quiz',
    label: 'Quiz',
    icon: IconQuestionMark,
    bgColor: 'bg-[#f0fdf4]',
    iconColor: 'text-emerald-500',
  },
  {
    id: 'upload-pdf',
    label: 'Upload PDF',
    icon: IconUpload,
    bgColor: 'bg-[#f8fafc]',
    iconColor: 'text-slate-400',
  },
  {
    id: 'youtube-video',
    label: 'YouTube',
    icon: IconBrandYoutube,
    bgColor: 'bg-[#fef2f2]',
    iconColor: 'text-red-400',
  },
  {
    id: 'notes',
    label: 'Notes',
    icon: IconFileText,
    bgColor: 'bg-[#f5f5f4]',
    iconColor: 'text-stone-500',
  },
];

interface ActionCardsPanelProps {
  onCardClick: (cardId: string) => void;
  onAISubmit?: (prompt: string) => void;
  autoFocusInput?: boolean;
}

export const ActionCardsPanel = ({ onCardClick, onAISubmit, autoFocusInput = false }: ActionCardsPanelProps) => {
  const [inputValue, setInputValue] = useState('');

  const handleCardClick = (card: ActionCard) => {
    onCardClick(card.id);
  };

  const handleSubmit = () => {
    if (inputValue.trim()) {
      onAISubmit?.(inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <>
      {/* Action Cards Grid - 3 columns on mobile, 5 on desktop */}
      <div data-tour="action-cards" className="grid grid-cols-3 md:grid-cols-5 gap-2 md:gap-3 w-full mb-6 md:mb-8">
        {actionCards.map((card) => {
          const Icon = card.icon;

          return (
            <button
              key={card.id}
              onClick={() => handleCardClick(card)}
              className={`relative flex flex-col items-center gap-1.5 md:gap-2 p-3 md:p-4 rounded-xl transition-all duration-200 group ${card.bgColor} hover:shadow-md hover:scale-[1.02]`}
            >
              <Icon className={`w-5 h-5 md:w-6 md:h-6 ${card.iconColor}`} />
              <span className={`font-medium text-xs md:text-sm ${card.iconColor} text-center leading-tight`}>
                {card.label}
              </span>
              {card.beta && (
                <span className="absolute top-1.5 right-1.5 md:top-2 md:right-2 px-1 md:px-1.5 py-0.5 text-[9px] md:text-[10px] font-semibold bg-slate-800 text-white rounded">
                  BETA
                </span>
              )}
              <div className="absolute top-1.5 right-1.5 md:top-2 md:right-2 p-1 rounded-full bg-white/50 opacity-0 group-hover:opacity-100 transition-opacity">
                <Plus className="w-3 h-3 text-slate-400" />
              </div>
            </button>
          );
        })}
      </div>

      {/* AI Prompt Input */}
      <div className="w-full">
        <div className="relative rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm focus-within:border-slate-300 transition-colors">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything... e.g., 'Explain how photosynthesis works'"
            className="w-full min-h-[80px] md:min-h-[120px] p-3 md:p-4 pr-3 md:pr-4 pb-14 md:pb-16 resize-none border-0 outline-none text-sm text-slate-700 placeholder:text-slate-400 bg-transparent"
            autoFocus={autoFocusInput}
          />

          <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-3">
            {/* Keyboard shortcut - only show on desktop */}
            <p className="hidden md:block text-xs text-slate-400">
              Press <kbd className="px-1 py-0.5 bg-slate-100 rounded text-xs">⌘</kbd> + <kbd className="px-1 py-0.5 bg-slate-100 rounded text-xs">Enter</kbd> to submit
            </p>
            <div className="flex-1 md:hidden" />

            {/* Ask button - icon only on mobile, full on desktop */}
            <button
              onClick={handleSubmit}
              disabled={!inputValue.trim()}
              className={`flex items-center justify-center gap-1.5 p-2 md:px-4 md:py-2 rounded-lg font-medium text-sm transition-all ${inputValue.trim()
                ? 'bg-slate-900 text-white hover:bg-slate-800'
                : 'bg-slate-100 text-slate-400'
                }`}
            >
              <Send className="w-4 h-4 md:hidden" />
              <IconSparkles className="w-4 h-4 hidden md:block" />
              <span className="hidden md:inline">Ask AI</span>
            </button>
          </div>
        </div>
      </div>

      {/* Helper text */}
      <p className="mt-4 text-xs text-slate-400 text-center">
        Select a card above to add content or ask AI anything
      </p>
    </>
  );
};

